import { useEffect } from "react";
import { io } from "socket.io-client";
import logger from "../utils/logger";
import { getSocketUrl, getSocketOptions } from "../utils/socketConfig";
import { REQUESTS_CHANGED_EVENT } from "../components/employee-requests/constants";

/**
 * Realtime sync for Employee Requests (admins only), on the app's existing
 * Socket.IO server. The server decides who may join the room; events carry
 * only { requestId, action }.
 *
 * To keep a single connection per tab, this hook is mounted once (Sidebar) and
 * re-broadcasts each event as a window event (REQUESTS_CHANGED_EVENT) that the
 * sidebar badge, the requests page and the open request popup all listen to.
 */
export function useEmployeeRequestsSocket({ token, enabled }) {
  useEffect(() => {
    if (!enabled || !token || !token.trim()) return undefined;

    const socket = io(getSocketUrl(), getSocketOptions(token, { reconnectionAttempts: Infinity }));
    let connectedBefore = false;

    const refreshAuth = () => {
      const latest = localStorage.getItem("accessToken");
      if (latest) socket.auth = { ...socket.auth, token: latest };
    };
    socket.io.on("reconnect_attempt", refreshAuth);

    socket.on("connect", () => {
      socket.emit("employeeRequests:subscribe", {}, (ack) => {
        if (!ack?.ok) return logger.error("[employeeRequests] subscribe failed", ack?.error);
        // Events aren't replayed: after a reconnect, refetch whatever was missed.
        if (connectedBefore) window.dispatchEvent(new CustomEvent(REQUESTS_CHANGED_EVENT, { detail: { action: "resync" } }));
        connectedBefore = true;
      });
    });
    socket.on("connect_error", (err) => {
      if (/token|auth/i.test(err?.message || "")) refreshAuth();
    });
    socket.on("employeeRequests:changed", (e) => {
      window.dispatchEvent(new CustomEvent(REQUESTS_CHANGED_EVENT, { detail: e }));
    });

    return () => {
      socket.emit("employeeRequests:unsubscribe");
      socket.disconnect();
    };
  }, [token, enabled]);
}

export default useEmployeeRequestsSocket;
